var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SessionService } from "./services/session-service";
import { MessageParser } from "./services/message-parser";
import { RedemptionService } from "./services/redemption-service";
import { APIClient } from "./data/api/api-client";
import { SessionAPI } from "./data/api/session-api";
import { ChatAPI } from "./data/api/chat-api";
import { AuthAPI } from "./data/api/auth-api";
import { RewardAPI } from "./data/api/reward-api";
import { FloatingButton } from "./views/components/button";
import { ChatPopup } from "./views/components/chat";
import { injectStyles } from "./views/shared/styles";
import { getEnv, SupportedNetwork, Environment } from "./core/config/env";
import { DevHelper } from "./core/dev-helper";
/**
 * Main SDK Class
 */
export class MeAgentSDK {
    constructor(config) {
        this.redemptionService = null;
        this.button = null;
        this.chat = null;
        this.devHelper = null;
        this.initialized = false;
        this.isOpen = false;
        this.validateConfig(config);
        this.config = Object.assign({ position: "bottom-right", environment: Environment.DEV, network: SupportedNetwork.SEPOLIA }, config);
        // Get environment configuration based on environment and network
        this.env = getEnv(this.config.environment, this.config.network);
        // Initialize API client
        this.apiClient = new APIClient(this.config, this.env);
        // Initialize services
        const sessionAPI = new SessionAPI(this.config, this.env);
        const chatAPI = new ChatAPI(this.config, this.env);
        const authAPI = new AuthAPI(this.config, this.env);
        const rewardAPI = new RewardAPI(this.config, this.env);
        this.sessionService = new SessionService(sessionAPI, chatAPI);
        this.messageParser = new MessageParser();
        // Initialize RedemptionService with network-specific configuration
        this.redemptionService = new RedemptionService(authAPI, rewardAPI, this.apiClient.redemptionAPI, {
            apiKey: this.env.MAGIC_PUBLISHABLE_API_KEY,
            chainId: this.env.CHAIN_ID,
            rpcUrl: this.env.RPC_URL,
        }, this.env.OPEN_REWARD_DIAMOND, parseInt(this.env.CHAIN_ID, 10), this.env.RPC_URL, this.env.RUNTIME_URL, this.env.ME_API_KEY, this.env.API_V1_URL, this.env.GELATO_API_KEY, this.config.brandId || "");
    }
    /**
     * Validate configuration
     */
    validateConfig(config) {
        if (!config.userId) {
            throw new Error("MeAgent SDK: userId is required. Please provide a userId in the configuration.");
        }
    }
    /**
     * Initialize the SDK
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.initialized) {
                console.warn("MeAgent SDK: Already initialized");
                return;
            }
            try {
                // Inject styles
                injectStyles();
                // Session will be auto-created on first message
                const sessionId = this.sessionService.getSessionId();
                // Initialize UI components
                this.button = new FloatingButton(this.config.position || "bottom-right", () => this.toggleChat());
                this.chat = new ChatPopup(this.config.position || "bottom-right", (message) => this.sendMessage(message), () => this.toggleChat(), this.apiClient, sessionId, this.config, this.redemptionService || undefined);
                // Set session switch callback
                this.chat.setOnSessionSwitch((newSessionId) => {
                    this.sessionService.setSessionId(newSessionId);
                });
                // Mount components
                this.button.mount();
                this.chat.mount();
                // Show welcome message
                this.chat.showWelcome();
                // Initialize dev helper if dev mode is enabled
                if (this.config.devMode) {
                    this.devHelper = new DevHelper(true, {
                        onShowOfferDetail: (offerCode, sessionId) => {
                            var _a;
                            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.devShowOfferDetail(offerCode, sessionId);
                        },
                        onShowBrandList: () => {
                            var _a;
                            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.devShowBrandList();
                        },
                        onShowCategoryGrid: () => {
                            var _a;
                            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.devShowCategoryGrid();
                        },
                    });
                }
                this.initialized = true;
            }
            catch (error) {
                console.error("MeAgent SDK: Initialization failed", error);
                throw error;
            }
        });
    }
    /**
     * Toggle chat open/closed
     */
    toggleChat() {
        var _a, _b, _c, _d;
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.show();
            (_b = this.button) === null || _b === void 0 ? void 0 : _b.hide();
        }
        else {
            (_c = this.chat) === null || _c === void 0 ? void 0 : _c.hide();
            (_d = this.button) === null || _d === void 0 ? void 0 : _d.show();
        }
    }
    /**
     * Programmatically open the chat widget
     * @public
     */
    open() {
        var _a, _b;
        if (!this.initialized) {
            console.warn("MeAgent SDK: Cannot open chat before initialization");
            return;
        }
        if (!this.isOpen) {
            this.isOpen = true;
            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.show();
            (_b = this.button) === null || _b === void 0 ? void 0 : _b.hide();
        }
    }
    /**
     * Programmatically close the chat widget
     * @public
     */
    close() {
        var _a, _b;
        if (!this.initialized) {
            console.warn("MeAgent SDK: Cannot close chat before initialization");
            return;
        }
        if (this.isOpen) {
            this.isOpen = false;
            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.hide();
            (_b = this.button) === null || _b === void 0 ? void 0 : _b.show();
        }
    }
    /**
     * Programmatically toggle the chat widget
     * @public
     */
    toggle() {
        if (!this.initialized) {
            console.warn("MeAgent SDK: Cannot toggle chat before initialization");
            return;
        }
        this.toggleChat();
    }
    /**
     * Update cart items dynamically
     * Call this after adding/removing items from cart to update SDK's cart state
     */
    updateCartItems(cartItems) {
        if (!this.initialized) {
            console.warn("MeAgent SDK: Cannot update cart items before initialization");
            return;
        }
        this.config.cartItems = cartItems;
        console.log("MeAgent SDK: Cart items updated", cartItems);
    }
    /**
     * Update liked offers dynamically
     * Call this after liking/unliking offers to update SDK's liked state
     */
    updateLikedOffers(likedOffers) {
        if (!this.initialized) {
            console.warn("MeAgent SDK: Cannot update liked offers before initialization");
            return;
        }
        this.config.likedOffers = likedOffers;
        console.log("MeAgent SDK: Liked offers updated", likedOffers);
    }
    /**
     * Send a message
     */
    sendMessage(content) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            // Add user message
            const userMessage = this.sessionService.createMessage("user", content);
            this.sessionService.addMessage(userMessage);
            (_a = this.chat) === null || _a === void 0 ? void 0 : _a.addMessage(userMessage);
            // Store the first message to use as title if this creates a new session
            const isFirstMessage = !this.sessionService.getSessionId();
            const firstMessageContent = isFirstMessage ? content : null;
            console.log("[SDK.sendMessage]", {
                isFirstMessage,
                firstMessageContent,
                currentSessionId: this.sessionService.getSessionId(),
            });
            // Show loading
            (_b = this.chat) === null || _b === void 0 ? void 0 : _b.setLoading(true);
            (_c = this.chat) === null || _c === void 0 ? void 0 : _c.showLoading();
            let assistantMessage = this.sessionService.createMessage("assistant", "");
            let isFirstChunk = true;
            let parsedData = {
                offers: [],
                brands: [],
                categories: [],
                searchCategories: [],
                products: [],
                showWaysToEarn: false,
            };
            let hasFinalMessage = false;
            try {
                yield this.apiClient.sendMessage(this.sessionService.getSessionId(), content, (chunk, rawData) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    console.log("[SDK] Chunk handler called:", {
                        chunk,
                        rawData,
                        isFirstChunk,
                    });
                    // Parse function calls and responses using MessageParser
                    if (rawData) {
                        const parsed = this.messageParser.parseMessageData(rawData);
                        if (parsed.offers.length > 0) {
                            parsedData.offers = parsed.offers;
                        }
                        if (parsed.brands.length > 0) {
                            parsedData.brands = parsed.brands;
                            console.log("[SDK] Detected signup earning brands:", parsed.brands.length);
                        }
                        if (parsed.products.length > 0) {
                            parsedData.products = parsed.products;
                            console.log("[SDK] Detected products:", parsed.products.length);
                        }
                        if (parsed.categories.length > 0) {
                            parsedData.categories = parsed.categories;
                            console.log("[SDK] Detected purchase categories:", parsed.categories.length);
                        }
                        if (parsed.searchCategories.length > 0) {
                            parsedData.searchCategories = parsed.searchCategories;
                            console.log("[SDK] Detected search categories:", parsed.searchCategories.length);
                        }
                        if (parsed.showWaysToEarn) {
                            parsedData.showWaysToEarn = true;
                            console.log("[SDK] Detected ways_to_earn function call");
                        }
                    }
                    // Create message container on first data, even if empty text
                    if (isFirstChunk) {
                        console.log("[SDK] First chunk - creating message with content:", chunk);
                        (_a = this.chat) === null || _a === void 0 ? void 0 : _a.removeLoading();
                        assistantMessage.content = chunk || "";
                        this.sessionService.addMessage(assistantMessage);
                        (_b = this.chat) === null || _b === void 0 ? void 0 : _b.addMessage(assistantMessage);
                        isFirstChunk = false;
                    }
                    else if (chunk !== undefined) {
                        // Check if this is a partial/streaming chunk or final complete message
                        const isPartial = (rawData === null || rawData === void 0 ? void 0 : rawData.partial) === true;
                        if (isPartial) {
                            // Streaming chunk (delta) - append it for real-time display
                            assistantMessage.content += chunk;
                            this.sessionService.updateLastMessage(assistantMessage.content);
                            (_c = this.chat) === null || _c === void 0 ? void 0 : _c.updateLastMessage(assistantMessage.content);
                        }
                        else if ((rawData === null || rawData === void 0 ? void 0 : rawData.response) !== undefined ||
                            ((_f = (_e = (_d = rawData === null || rawData === void 0 ? void 0 : rawData.content) === null || _d === void 0 ? void 0 : _d.parts) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.text)) {
                            // Final complete message (new format: rawData.response, old format: rawData.content.parts[0].text)
                            if (hasFinalMessage) {
                                // We already have a final message, so this is additional text
                                // after a function call - append it with spacing
                                assistantMessage.content += "\n\n" + chunk;
                            }
                            else {
                                // First final message - replace to ensure accuracy
                                assistantMessage.content = chunk;
                                hasFinalMessage = true;
                            }
                            this.sessionService.updateLastMessage(assistantMessage.content);
                            (_g = this.chat) === null || _g === void 0 ? void 0 : _g.updateLastMessage(assistantMessage.content);
                        }
                    }
                }, (returnedSessionId) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                    // On complete - update session ID if this is the first message
                    if (returnedSessionId && !this.sessionService.getSessionId()) {
                        this.sessionService.setSessionId(returnedSessionId);
                        console.log("[SDK.onComplete] Calling setSessionId with:", {
                            returnedSessionId,
                            firstMessageContent,
                        });
                        (_a = this.chat) === null || _a === void 0 ? void 0 : _a.setSessionId(returnedSessionId, firstMessageContent || undefined);
                        console.log("MeAgent SDK: Session created:", returnedSessionId);
                    }
                    (_b = this.chat) === null || _b === void 0 ? void 0 : _b.setLoading(false);
                    (_c = this.chat) === null || _c === void 0 ? void 0 : _c.removeLoading();
                    // Show offer preview if offers were found
                    if (parsedData.offers.length > 0) {
                        (_d = this.chat) === null || _d === void 0 ? void 0 : _d.showOfferPreview(parsedData.offers);
                    }
                    // Show brand preview if brands were found
                    if (parsedData.brands.length > 0) {
                        (_e = this.chat) === null || _e === void 0 ? void 0 : _e.showBrandPreview(parsedData.brands);
                    }
                    // Show product preview if products were found
                    if (parsedData.products.length > 0) {
                        (_f = this.chat) === null || _f === void 0 ? void 0 : _f.showProductPreview(parsedData.products);
                    }
                    // Show category preview if categories were found
                    if (parsedData.categories.length > 0) {
                        (_g = this.chat) === null || _g === void 0 ? void 0 : _g.showCategoryPreview(parsedData.categories);
                    }
                    // Show search category card list if search categories were found
                    if (parsedData.searchCategories.length > 0) {
                        (_h = this.chat) === null || _h === void 0 ? void 0 : _h.showSearchCategoryCardList(parsedData.searchCategories);
                    }
                    // Show ways to earn quick actions if function was called
                    if (parsedData.showWaysToEarn) {
                        console.log("[SDK] Showing ways to earn actions");
                        (_j = this.chat) === null || _j === void 0 ? void 0 : _j.showWaysToEarnActions();
                    }
                }, (error) => {
                    var _a, _b, _c;
                    // On error
                    console.error("MeAgent SDK: Error sending message", error);
                    (_a = this.chat) === null || _a === void 0 ? void 0 : _a.setLoading(false);
                    (_b = this.chat) === null || _b === void 0 ? void 0 : _b.removeLoading();
                    const errorMessage = this.sessionService.createMessage("assistant", "Sorry, something went wrong. Please try again.");
                    this.sessionService.addMessage(errorMessage);
                    (_c = this.chat) === null || _c === void 0 ? void 0 : _c.addMessage(errorMessage);
                });
            }
            catch (error) {
                console.error("MeAgent SDK: Error in sendMessage", error);
                (_d = this.chat) === null || _d === void 0 ? void 0 : _d.setLoading(false);
            }
        });
    }
    /**
     * Destroy the SDK
     */
    destroy() {
        var _a, _b;
        (_a = this.button) === null || _a === void 0 ? void 0 : _a.unmount();
        (_b = this.chat) === null || _b === void 0 ? void 0 : _b.unmount();
        this.initialized = false;
    }
}
